import React, { useMemo, useCallback } from "react";
import {
  useActivePartKey,
  useProductActions,
  type OrderCodePartOptionStrict,
  type OrderCodePartStrict,
} from "stores/product";
import { RangeSelectInput } from "./inputs/RangeSelectInput";
import { RangeInput } from "./inputs/RangeInput";
import { SelectInput } from "./inputs/SelectInput";
import { SelectColorInput } from "./inputs/SelectColorInput/SelectColorInput";
import { RadioInput } from "./inputs/RadioInput";
import cx from "classnames";
import styles from "../../../ProductConfigurator.module.scss";
import { useIsPartOptionDisabled } from "components/ProductConfigurator/hooks/useIsPartOptionDisabled";

enum InputType {
  RangeSelect,
  Range,
  Select,
  SelectColor,
  Radio,
}

export type OnChange = (value: number | string | null) => void;

export const OrderCodePartOption = ({
  option,
  part,
  disabled,
  isModal,
}: {
  option: OrderCodePartOptionStrict;
  part: OrderCodePartStrict;
  disabled: boolean;
  isModal?: boolean;
}) => {
  const actions = useProductActions();
  const isPartDisabled = useIsPartOptionDisabled(part, option);

  const onChange: OnChange = useCallback(
    (value) => {
      actions.selectPart({
        partKey: part.key,
        optionKey: option.key,
        signatureKey: value !== null ? String(value) : null,
      });
    },
    [actions, part.key, option.key],
  );
  const isDisabled = disabled || isPartDisabled || option.state === 1;

  const inputType = getInputType(option);

  const props = useMemo(
    () => ({
      option,
      disabled: isDisabled,
      onChange,
      part,
      isModal,
    }),
    [option, part, isDisabled, onChange, isModal],
  );

  return (
    <OrderCodePartOptionLayout option={option} part={part}>
      {inputType === InputType.RangeSelect && <RangeSelectInput {...props} />}
      {inputType === InputType.Range && <RangeInput {...props} />}
      {inputType === InputType.SelectColor && <SelectColorInput {...props} />}
      {inputType === InputType.Select && <SelectInput {...props} />}
      {inputType === InputType.Radio && <RadioInput {...props} />}
    </OrderCodePartOptionLayout>
  );
};

const OrderCodePartOptionLayout = ({
  children,
  option,
  part,
}: {
  children: React.ReactNode;
  option: OrderCodePartOptionStrict;
  part: OrderCodePartStrict;
}) => {
  const activePartKey = useActivePartKey();
  const isActive = activePartKey === part.key; // TODO: determine active state store ui
  const isModalView = false; // TODO: determine modal view from store ui
  return (
    <div
      className={cx(
        "d-flex flex-column flex-grow-1 flex-shrink-2 px-4 py-3 bg-white border rounded-2",
        {
          "bg-gray-100": isModalView,
          "border-info border": isActive,
          "border-gray-300": !isActive,
          "border-white": !isActive && !isModalView,
        },
        styles.card,
      )}
      key={option.key}
    >
      {children}
    </div>
  );
};

const getInputType = (option: OrderCodePartOptionStrict): InputType => {
  let inputType: InputType = InputType.Radio;

  if (option.range !== null) {
    const visibleSignatures =
      option.signatures?.filter(
        (signature) => signature && signature.state !== 2,
      ) || [];
    if (visibleSignatures.length > 3) {
      inputType = InputType.RangeSelect;
    } else {
      inputType = InputType.Range;
    }
  } else if (option.signatures && option.signatures.length > 6) {
    if (option?.presentation?.includes("RAL ")) {
      inputType = InputType.SelectColor;
    } else {
      inputType = InputType.Select;
    }
  }

  return inputType;
};
